import { Link } from "react-router-dom";
import type { ReactNode } from "react";

import { Badge } from "../../../ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { useModelContext, type ModelRegistryEntry } from "../lib/api/model";

type StepNum = 1 | 2 | 3 | 4;

interface StepDef {
  num: StepNum;
  label: string;
  to: string;
}

const STEPS: StepDef[] = [
  { num: 1, label: "Health", to: "/model-drift-monitoring" },
  { num: 2, label: "Input Stability", to: "/model-drift-monitoring/input-stability" },
  { num: 3, label: "Observability", to: "/model-drift-monitoring/observability" },
  { num: 4, label: "Risk Appetite", to: "/model-drift-monitoring/risk-appetite" },
];

function formatCohort(c: string): string {
  const [y, m] = c.split("-").map(Number);
  if (!y || !m) return c;
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${months[m - 1]} ${y} cohort`;
}

function Breadcrumb({ active }: { active: StepNum }) {
  return (
    <nav
      className="py-3 px-6 overflow-x-auto fixed top-[110px] left-0 z-40"
      style={{
        backgroundColor: "#191e27",
        right: "var(--rightNavWidth, 0px)",
        transition: "right 0.2s ease",
      }}
    >
      <div className="flex items-center gap-1 min-w-max">
        {STEPS.map((s, i) => {
          const isActive = s.num === active;
          const isDone = s.num < active;
          return (
            <div key={s.num} className="flex items-center">
              <Link
                to={s.to}
                className={[
                  "flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors",
                  isActive ? "text-primary bg-primary/10" : isDone ? "text-foreground" : "text-muted-foreground",
                ].join(" ")}
              >
                <span
                  className={[
                    "w-5 h-5 flex items-center justify-center text-[10px] border",
                    isActive
                      ? "bg-primary border-primary text-primary-foreground"
                      : isDone
                        ? "bg-success/20 border-success text-success"
                        : "border-muted-foreground/50",
                  ].join(" ")}
                >
                  {s.num}
                </span>
                <span>{s.label}</span>
              </Link>
              {i < STEPS.length - 1 && (
                <div className={["w-4 h-px", isDone ? "bg-primary" : "bg-border"].join(" ")} />
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

function ModelHeader({
  modelId,
  segment,
  models,
  onModelChange,
}: {
  modelId: number;
  segment?: string;
  models?: ModelRegistryEntry[];
  onModelChange?: (id: number) => void;
}) {
  const { data } = useModelContext(modelId);
  if (!data) {
    return <div className="h-8 animate-pulse rounded-md bg-muted/30" />;
  }

  const segmentLabel = segment
    ? segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()
    : data.productType;

  const hasModelSelector = models && models.length > 0 && onModelChange;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {hasModelSelector ? (
          <Select value={String(modelId)} onValueChange={(v) => onModelChange(Number(v))}>
            <SelectTrigger className="h-7 w-[180px] text-xs font-semibold border-border bg-transparent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {models.map((m) => (
                <SelectItem key={m.modelId} value={String(m.modelId)}>
                  {m.modelName} v{m.modelVersion}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="font-semibold text-foreground">
            {data.modelName} v{data.modelVersion}
          </span>
        )}
        <span className="text-muted-foreground">·</span>
        <span className="text-foreground/80">{segmentLabel}</span>
        <Tooltip>
          <TooltipTrigger>
            <Badge className="bg-amber-dim text-amber border-transparent hover:bg-amber-dim cursor-default">
              MRM Tier {data.modelTier} (High)
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            Highest governance, drives lending decisions
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="text-xs text-muted-foreground">
        Last run: {formatCohort(data.lastRunCohort)} ·{" "}
        {data.cohortsMonitored} cohorts monitored
      </div>
    </div>
  );
}

export function StepShell({
  step,
  children,
  modelId = 1,
  segment,
  models,
  onModelChange,
}: {
  step: StepNum;
  children: ReactNode;
  modelId?: number;
  segment?: string;
  models?: ModelRegistryEntry[];
  onModelChange?: (id: number) => void;
}) {
  const prev = STEPS.find((s) => s.num === step - 1);
  const next = STEPS.find((s) => s.num === step + 1);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <div className="pl-40 pr-64 pt-[175px] pb-24 space-y-[10px]">
        <Breadcrumb active={step} />
        <ModelHeader
          modelId={modelId}
          segment={segment}
          models={models}
          onModelChange={onModelChange}
        />
        <div>{children}</div>
      </div>

      {/* Fixed bottom footer */}
      <footer
        className="fixed bottom-0 left-0 z-50 px-6 py-2"
        style={{
          backgroundColor: "#191e27",
          right: "var(--rightNavWidth, 0px)",
          transition: "right 0.2s ease",
        }}
      >
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Step {step} of {STEPS.length}
          </span>

          <div className="flex gap-4">
            {prev ? (
              <Link
                to={prev.to}
                className="px-6 py-2 border border-border text-foreground font-medium min-w-[100px] text-center hover:border-amber transition-colors"
              >
                {prev.label}
              </Link>
            ) : (
              <div className="px-6 py-2 min-w-[100px]" />
            )}

            {next ? (
              <Link
                to={next.to}
                className="px-6 py-2 bg-amber text-background font-medium min-w-[100px] text-center hover:bg-amber/90 transition-colors"
              >
                {next.label}
              </Link>
            ) : (
              <div className="px-6 py-2 min-w-[100px]" />
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
